import {
  IconX,
  IconCalendar,
  IconGasStation,
  IconDashboard,
  IconFileText,
  IconClipboardList,
  IconDownload,
  IconCar,
  IconFileDescription,
  IconGauge,
  IconId,
  IconCloudDown,
  IconEdit,
} from "@tabler/icons-react";
import React, { useState } from "react";
import { T, useLanguage } from "../../../context/LanguageContext";
import formatDate, { getFileNameFromUrl } from "../../../utilities/helper";
import { usePermission } from "../../../hooks/usePermissionTabs";
import RequestsVehiclesManagement from "./RequestsVehiclesManagement";
import Odometer from "./Odometer";
import Fuel from "./Fuel";
import { useForm } from "react-hook-form";
import {
  useUpdateVehicleMutation,
  useVehicleAssignMutation,
  useVehicleCreateMutation,
} from "../../../apis/fleetApis";
import AddFleetModal from "./AddFleetModal";
import AssignModal from "./AssignModal";
import toast from "react-hot-toast";
import { FormValues, SelectedFilesState } from "./FleetVehiclesManagement";

type Props = {
  open: boolean;
  onClose: () => void;
  vehicle?: any;
  onAssign?: () => void;
  isAddMode?: boolean;
};

const formatStatus = (status: string) => {
  return status
    ?.toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getStatusBadgeStyles = (status: string) => {
  switch (status) {
    case "ACTIVE":
    case "AVAILABLE":
      return "bg-emerald-500/15 text-emerald-600 border-emerald-500/20";
    case "INACTIVE":
      return "bg-slate-500/15 text-slate-600 border-slate-500/20";
    case "ASSIGNED":
      return "bg-blue-500/15 text-blue-600 border-blue-500/20";
    case "MAINTENANCE":
      return "bg-amber-500/15 text-amber-600 border-amber-500/20";
    case "ACCIDENT":
      return "bg-rose-500/15 text-rose-600 border-rose-500/20";
    case "BREAKDOWN":
      return "bg-violet-500/15 text-violet-600 border-violet-500/20";
    default:
      return "bg-gray-500/15 text-gray-600 border-gray-500/20";
  }
};

const Row = ({
  icon,
  label,
  value,
  className,
}: {
  icon?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="flex items-center gap-1">
      <span className="font-medium text-xs text-[#212121]">
        <T>{label}</T>:{" "}
      </span>
    </div>
    <div className="flex items-center gap-2">
      <span className="break-all text-xs text-gray-700 font-medium">
        {value}
      </span>
    </div>
  </div>
);

const tabs = [
  {
    label: "Details",
    icon: IconFileDescription,
  },
  {
    label: "Requests",
    icon: IconClipboardList,
  },
  {
    label: "Odometer",
    icon: IconGauge,
  },
  {
    label: "Fuel",
    icon: IconGasStation,
  },
];

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      <T>{title}</T>
    </p>
  </div>
);

const VehicleViewModal = ({
  open,
  onClose,
  vehicle,
  onAssign,
  isAddMode,
}: Props) => {
  const { t } = useLanguage();
  const { allowed: canUpdateVehicle } = usePermission("vehicles", "update");
  const [activeTab, setActiveTab] = useState("Details");
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [editDialog, setEditDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFilesState>({
    images: false,
    imagesOdoMeter: false,
    rcDocs: false,
    inspectionPaper: false,
    registrationDocumentUrl: false,
    insuranceDocumentUrl: false,
  });

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>();

  const {
    register: assignRegister,
    handleSubmit: handleAssignSubmit,
    clearErrors: clearAssignErrors,
    reset: assignReset,
    setValue: setAssignValue,
    watch: assignWatch,
    control: assignControl,
    formState: { errors: assignErrors },
  } = useForm<any>();

  const { mutate: updateVehicle, isPending: updatePending } =
    useUpdateVehicleMutation();
  const { mutate: assignVehicle, isPending: assignPending } =
    useVehicleAssignMutation();

  const handleEditClick = () => {
    setEditDialog(true);
    setValue("name", vehicle?.name || "");
    setValue(
      "makeName",
      vehicle?.makeName || vehicle?.make_name || vehicle?.name || "",
    );
    setValue(
      "vehicleType",
      vehicle?.vehicle_type || vehicle?.vehicleType || "",
    );
    const yearVal = vehicle?.year
      ? vehicle.year.includes("-")
        ? String(new Date(vehicle.year).getFullYear())
        : String(vehicle.year)
      : "";
    setValue("year", yearVal);
    setValue("model", vehicle?.model || "");
    setValue("makeId", vehicle?.makeId || vehicle?.make_id || "");
    setValue("modelId", vehicle?.modelId || vehicle?.model_id || "");
    setValue("modelName", vehicle?.modelName || vehicle?.model_name || vehicle?.model || "");
    setValue("vehicleNumber", vehicle?.vehicle_number || "");
    setValue("fuelType", (vehicle?.fuel_type || vehicle?.fuelType || "")?.toLowerCase());
    setValue("odometer", vehicle?.odometer || "");
    setValue("rcNumber", vehicle?.rc_number || "");
    setValue("imagesOdoMeter", vehicle?.images_odo_meter || vehicle?.imagesOdoMeter || "");
    setValue("rcDocs", vehicle?.rc_docs || vehicle?.rcDocs || "");
    setValue("images", vehicle?.images || []);

    setValue("registrationType", vehicle?.registrationType || vehicle?.registration_type || "");
    setValue("color", vehicle?.color || "");
    setValue("sequenceNumber", vehicle?.sequenceNumber || vehicle?.sequence_number || "");
    setValue("status", vehicle?.status || "ACTIVE");
    
    const formatInputDate = (dateStr?: string) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return d.toISOString().split("T")[0];
    };

    setValue("inspectionExpiryDate", formatInputDate(vehicle?.inspectionExpiryDate || vehicle?.inspection_expiry_date));
    setValue("registrationExpiryDate", formatInputDate(vehicle?.registrationExpiryDate || vehicle?.registration_expiry_date));
    setValue("insuranceCompany", vehicle?.insuranceCompany || vehicle?.insurance_company || "");
    setValue("insuranceStartDate", formatInputDate(vehicle?.insuranceStartDate || vehicle?.insurance_start_date));
    setValue("insuranceEndDate", formatInputDate(vehicle?.insuranceEndDate || vehicle?.insurance_end_date));
    
    setValue("inspectionPaper", vehicle?.inspection_paper || vehicle?.inspectionPaper || "");
    setValue("registrationDocumentUrl", vehicle?.registration_document_url || vehicle?.registrationDocumentUrl || "");
    setValue("insuranceDocumentUrl", vehicle?.insurance_document_url || vehicle?.insuranceDocumentUrl || "");
  };

  const handleUpdateVehicle = (data: any) => {
    let { rcDocs, imagesOdoMeter, images, inspectionPaper, registrationDocumentUrl, insuranceDocumentUrl, ...otherData } = data;
    const payload = {
      id: vehicle?.id,
      ...otherData,
      ...(watch("imagesOdoMeter") && { imagesOdoMeter: true }),
      ...(watch("rcDocs") && { rcDocs: true }),
      ...(watch("images") && { images: true }),
      ...(watch("inspectionPaper") && { inspectionPaper: true }),
      ...(watch("registrationDocumentUrl") && { registrationDocumentUrl: true }),
      ...(watch("insuranceDocumentUrl") && { insuranceDocumentUrl: true }),
      ...(selectedFiles?.rcDocs && { rcDocs: data?.rcDocs }),
      ...(selectedFiles?.imagesOdoMeter && {
        imagesOdoMeter: data?.imagesOdoMeter,
      }),
      ...(selectedFiles?.images && { images: data?.images }),
      ...(selectedFiles?.inspectionPaper && { inspectionPaper: data?.inspectionPaper }),
      ...(selectedFiles?.registrationDocumentUrl && { registrationDocumentUrl: data?.registrationDocumentUrl }),
      ...(selectedFiles?.insuranceDocumentUrl && { insuranceDocumentUrl: data?.insuranceDocumentUrl }),
    };

    updateVehicle(payload, {
      onSuccess: () => {
        setEditDialog(false);
        reset();
        setSelectedFiles({
          images: false,
          imagesOdoMeter: false,
          rcDocs: false,
          inspectionPaper: false,
          registrationDocumentUrl: false,
          insuranceDocumentUrl: false,
        });
        toast.success(t("Vehicle Update Successfully."));
        window.dispatchEvent(new CustomEvent("refetchVehicles"));
      },
      onError: (error: any) => {
        const errorData = error.response?.data;
        const errorMessage =
          errorData?.message ||
          errorData?.error ||
          "Error occurred during updating.";
        toast.error(t(errorMessage));
      },
    });
  };

  const handleAssignVehicle = (data: any) => {
    assignVehicle(
      { ...data, vehicleId: vehicle?.id },
      {
        onSuccess: () => {
          setAssignDialog(false);
          assignReset();
          toast.success(t("Vehicle Assigned Successfully."));
          window.dispatchEvent(new CustomEvent("refetchVehicles"));
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to Assign Vehicle";
          toast.error(t(errorMessage));
        },
      },
    );
  };

  const handleClose = () => {
    if (editDialog) {
      setEditDialog(false);
      reset();
      setSelectedFiles({
        images: false,
        imagesOdoMeter: false,
        rcDocs: false,
        inspectionPaper: false,
        registrationDocumentUrl: false,
        insuranceDocumentUrl: false,
      });
    } else if (assignDialog) {
      setAssignDialog(false);
      assignReset();
    } else {
      onClose();
    }
  };

  const openInNewTab = (url?: string) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const { mutate: createVehicle, isPending: createPending } =
    useVehicleCreateMutation();

  const handleCreateVehicle = (data: any) => {
    createVehicle(
      { ...data },
      {
        onSuccess: () => {
          onClose();
          window.dispatchEvent(new CustomEvent("refetchVehicles"));
          toast.success(t("Vehicle created successfully"));
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create vehicle";
          toast.error(t(errorMessage));
        },
      },
    );
  };

  if (!open || (!vehicle && !isAddMode)) return null;
  const isVehicleAssigned = vehicle?.assignStatus === "ASSIGNED";

  const images = vehicle?.images || [];
  const primaryImage = images[activeImageIndex] || null;

  return (
    <div className="relative rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white/50">
        <div className="flex items-center gap-3">
          {isAddMode ? (
            <>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-100 overflow-hidden shrink-0">
                <IconCar className="text-gray-400" size={20} />
              </div>
              <div>
                <h2 className="text-base sm:text-xl font-semibold text-[#212121]">
                  <T>Add New Vehicle</T>
                </h2>
                <p className="text-xs sm:text-sm text-[#212121]/70">
                  <T>Enter vehicle details below</T>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-100 overflow-hidden shrink-0">
                {images.length > 0 ? (
                  <img
                    src={images[0]}
                    alt="Vehicle Thumbnail"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <IconFileText className="text-gray-400" />
                )}
              </div>
              <div>
                <h2 className="text-base sm:text-xl font-semibold text-[#212121]">
                  {vehicle?.name}
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-xs sm:text-sm text-[#212121]/70">
                    {vehicle?.vehicle_number || "V-001"}
                  </p>
                  <span
                    className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider border ${
                      vehicle?.assignStatus === "ASSIGNED"
                        ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                        : "bg-amber-50 text-amber-600 border-amber-200"
                    }`}
                  >
                    {t(
                      vehicle?.assignStatus?.replace("_", " ") ||
                        "NOT ASSIGNED",
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {isAddMode || editDialog || assignDialog ? (
            <button
              onClick={
                isAddMode
                  ? handleSubmit(handleCreateVehicle)
                  : editDialog
                    ? handleSubmit(handleUpdateVehicle)
                    : handleAssignSubmit(handleAssignVehicle)
              }
              disabled={
                isAddMode
                  ? createPending
                  : editDialog
                    ? updatePending
                    : assignPending
              }
              className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-[#FBD037] text-[#212121] text-xs sm:text-sm font-semibold rounded-lg hover:bg-[#f5c71a] transition-colors disabled:opacity-50"
            >
              {(
                isAddMode
                  ? createPending
                  : editDialog
                    ? updatePending
                    : assignPending
              )
                ? isAddMode
                  ? t("Adding...")
                  : editDialog
                    ? t("Updating...")
                    : t("Assigning...")
                : isAddMode
                  ? t("Add Vehicle")
                  : editDialog
                    ? t("Update")
                    : t("Assign")}
            </button>
          ) : (
            <>
              {vehicle?.assignStatus === "NOT_ASSIGNED" && canUpdateVehicle && (
                <button
                  onClick={() => setAssignDialog(true)}
                  className="px-2.5 sm:px-4 py-1.5 sm:py-2 border border-[#FBD037] text-[#FBD037] bg-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-[#FBD037]/10 transition-colors"
                >
                  <T>Assign</T>
                </button>
              )}
              {canUpdateVehicle && (
                <button
                  onClick={() => {
                    handleEditClick();
                  }}
                  className="p-1.5 sm:p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                >
                  <IconEdit size={18} />
                </button>
              )}
            </>
          )}
          <button
            onClick={handleClose}
            className="p-1.5 sm:p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-clean">
        {isAddMode || editDialog ? (
          <div className="py-2">
            <AddFleetModal
              register={register as any}
              errors={errors as any}
              setValue={setValue as any}
              watch={watch as any}
              clearErrors={clearErrors as any}
              control={control as any}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
            />
          </div>
        ) : assignDialog ? (
          <div className="p-6">
            <AssignModal
              register={assignRegister}
              errors={assignErrors}
              setValue={setAssignValue as any}
              watch={assignWatch}
              clearErrors={clearAssignErrors as any}
              control={assignControl}
            />
          </div>
        ) : (
          <>
            <div className="flex gap-1.5 px-2 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white/30 overflow-x-auto no-scrollbar scrollbar-none">
              {tabs.map((tab: any) => {
                const Icon = tab.icon;

                return (
                  <button
                    key={tab.label}
                    onClick={() => setActiveTab(tab.label)}
                    className={`py-1.5 sm:py-2 px-2.5 text-[10px] min-[380px]:text-xs sm:text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-1 sm:gap-2 border shrink-0 flex-1 ${
                      activeTab === tab.label
                        ? "bg-[#FBD037]/20 text-[#000000] shadow-sm border-[#FBD037]"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}
                  >
                    <Icon
                      size={14}
                      className="shrink-0 hidden min-[360px]:block sm:w-4 sm:h-4"
                    />
                    <span className="whitespace-nowrap">
                      <T>{tab.label}</T>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto  ">
              {activeTab === "Details" && (
                <div className="p-6 space-y-4">
                  {/* Image Gallery */}
                  <div className="space-y-3">
                    <div className="w-full h-[240px] bg-white/20 rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex items-center justify-center relative">
                      {primaryImage ? (
                        <img
                          src={primaryImage}
                          alt="Vehicle Primary"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-gray-400 flex flex-col items-center">
                          <IconFileText size={48} className="mb-2 opacity-50" />
                          <span>
                            <T>No Image Available</T>
                          </span>
                        </div>
                      )}
                    </div>

                    {images.length > 1 && (
                      <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                        {images.map((img: string, index: number) => (
                          <button
                            key={index}
                            onClick={() => setActiveImageIndex(index)}
                            className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${activeImageIndex === index ? "border-[#FBD037] opacity-100" : "border-transparent opacity-60 hover:opacity-100"}`}
                          >
                            <img
                              src={img}
                              alt={`Thumbnail ${index}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Config details */}
                  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl  p-4 transition-all duration-300 space-y-3 relative">
                    <SectionHeader icon={IconCar} title="Vehicle Details" />
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                    <div className="grid grid-cols-5 gap-y-2">
                      <Row label="Company" value={vehicle.makeName || vehicle.make_name || vehicle.name || "—"} />
                      <Row
                        label="Type"
                        value={t(formatStatus(vehicle.vehicleType || vehicle.vehicle_type || ""))}
                      />
                      <Row label="Model" value={vehicle.model || "—"} />

                      <Row
                        label="Year"
                        value={
                          vehicle.year
                            ? String(vehicle.year).includes("-")
                              ? new Date(vehicle.year).getFullYear()
                              : vehicle.year
                            : t("N/A")
                        }
                      />
                      <Row
                        label="Fuel"
                        value={
                          (vehicle.fuelType || vehicle.fuel_type)
                            ? t(formatStatus(vehicle.fuelType || vehicle.fuel_type))
                            : "—"
                        }
                      />
                      <Row label="Plate No" value={vehicle.vehicleNumber || vehicle.vehicle_number || "—"} />
                      <Row label="Reg type" value={vehicle.registrationType || vehicle.registration_type || "—"} />
                      <Row label="Color" value={vehicle.color || "—"} />
                      <Row label="Seq No" value={vehicle.sequenceNumber || vehicle.sequence_number || "—"} />
                      <Row
                        label="Odometer"
                        value={
                          <span>
                            {vehicle.odometer || "0"} <T>KM</T>
                          </span>
                        }
                      />
                      <Row
                        label="Status"
                        value={
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusBadgeStyles(vehicle.status)}`}>
                            {t(formatStatus(vehicle.status || "ACTIVE"))}
                          </span>
                        }
                      />
                    </div>
                  </div>
                  {/* Documents */}
                  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl p-4 transition-all duration-300 space-y-3 relative">
                    <SectionHeader icon={IconClipboardList} title="Documents" />
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                    <div className="grid grid-cols-3 gap-y-2">
                      <Row
                        label="Vechile Id"
                        value={
                          <div className="flex flex-col gap-1">
                            <span>{vehicle.rc_number || "—"}</span>
                            {(vehicle.rc_docs || vehicle.rcDocs) && (
                              <span
                                className="text-blue-600 hover:underline cursor-pointer"
                                onClick={() => openInNewTab(vehicle.rc_docs || vehicle.rcDocs)}
                              >
                                <T>View Document</T>
                              </span>
                            )}
                          </div>
                        }
                      />

                      {isVehicleAssigned && (
                        <Row
                          label="Inspection Paper"
                          value={
                            <div className="flex flex-col gap-1">
                              <span>
                                {vehicle.inspection_paper || vehicle.inspectionPaper
                                  ? t("Uploaded")
                                  : t("Pending")}
                              </span>
                              {(vehicle.inspection_paper || vehicle.inspectionPaper) && (
                                <span
                                  className="text-blue-600 hover:underline cursor-pointer"
                                  onClick={() =>
                                    openInNewTab(vehicle.inspection_paper || vehicle.inspectionPaper)
                                  }
                                >
                                  <T>View Document</T>
                                </span>
                              )}
                            </div>
                          }
                        />
                      )}

                      <Row
                        label="Odometer Image"
                        value={
                          (vehicle.images_odo_meter || vehicle.imagesOdoMeter) ? (
                            <span
                              className="text-blue-600 hover:underline cursor-pointer"
                              onClick={() => openInNewTab(vehicle.images_odo_meter || vehicle.imagesOdoMeter)}
                            >
                              <T>View Document</T>
                            </span>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <Row
                        label="Inspection Exp date"
                        value={
                          <div className="flex flex-col gap-1">
                            <span>{formatDate(vehicle.inspectionExpiryDate || vehicle.inspection_expiry_date) || "—"}</span>
                            {(vehicle.inspection_paper || vehicle.inspectionPaper) && (
                              <span
                                className="text-blue-600 hover:underline cursor-pointer"
                                onClick={() => openInNewTab(vehicle.inspection_paper || vehicle.inspectionPaper)}
                              >
                                <T>View Document</T>
                              </span>
                            )}
                          </div>
                        }
                      />
                      <Row
                        label="Vechile Reg Exp date"
                        value={
                          <div className="flex flex-col gap-1">
                            <span>{formatDate(vehicle.registrationExpiryDate || vehicle.registration_expiry_date) || "—"}</span>
                            {(vehicle.registration_document_url || vehicle.registrationDocumentUrl) && (
                              <span
                                className="text-blue-600 hover:underline cursor-pointer"
                                onClick={() => openInNewTab(vehicle.registration_document_url || vehicle.registrationDocumentUrl)}
                              >
                                <T>View Document</T>
                              </span>
                            )}
                          </div>
                        }
                      />
                    </div>
                  </div>

                  {/* insurance  */}
                  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl  p-4 transition-all duration-300 space-y-3 relative">
                    <SectionHeader icon={IconCar} title="insurance Details" />
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                    <div className="grid grid-cols-3">
                      <Row label="Company" value={vehicle.insuranceCompany || vehicle.insurance_company || "—"} />
                      <Row label="Start Date" value={formatDate(vehicle.insuranceStartDate || vehicle.insurance_start_date) || "—"} />
                      <Row
                        label="End Date"
                        value={
                          <div className="flex flex-col gap-1">
                            <span>{formatDate(vehicle.insuranceEndDate || vehicle.insurance_end_date) || "—"}</span>
                            {(vehicle.insurance_document_url || vehicle.insuranceDocumentUrl) && (
                              <span
                                className="text-blue-600 hover:underline cursor-pointer"
                                onClick={() => openInNewTab(vehicle.insurance_document_url || vehicle.insuranceDocumentUrl)}
                              >
                                <T>View Document</T>
                              </span>
                            )}
                          </div>
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Requests" && (
                <div className="p-4">
                  <RequestsVehiclesManagement vehicleId={vehicle?.id} />
                </div>
              )}

              {activeTab === "Odometer" && (
                <div className="p-4">
                  <Odometer vehicleId={vehicle?.id} />
                </div>
              )}

              {activeTab === "Fuel" && (
                <div className="p-4">
                  <Fuel vehicleId={vehicle?.id} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VehicleViewModal;
